"use client";

import type { ReactNode } from "react";

import { cn } from "../../utils/cn";

type MainButtonProps = {
  children: ReactNode;
  onClick?: () => void;
  variant?: "primary" | "secondary" | "dark";
  size?: "sm" | "md" | "lg";
  className?: string;
  disabled?: boolean;
  type?: "button" | "submit" | "reset";
  /** Overrides `main-button` when this one button is what a spec targets. */
  "data-testid"?: string;
};

export function MainButton({
  children,
  onClick,
  variant = "primary",
  size = "md",
  className,
  disabled,
  type = "button",
  "data-testid": testId = "main-button",
}: MainButtonProps) {
  return (
    <button
      type={type}
      onClick={onClick}
      data-testid={testId}
      disabled={disabled}
      className={cn(
        "inline-flex items-center justify-center rounded-xl font-semibold",
        "transition-all duration-shell ease-shell",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:ring-offset-2",
        {
          "bg-primary text-pureWhite shadow-sm hover:bg-primary-hover hover:shadow-shell-md active:scale-[0.98]":
            variant === "primary",
          "bg-secondary text-text-primary hover:bg-secondary/80 active:scale-[0.98]":
            variant === "secondary",
          "bg-dark text-pureWhite hover:bg-dark-hover active:scale-[0.98]":
            variant === "dark",
          "px-3 py-1.5 text-base": size === "sm",
          "px-4 py-2 text-base": size === "md",
          "px-6 py-3 text-base": size === "lg",
          "pointer-events-none opacity-50": disabled,
        },
        className,
      )}
    >
      {children}
    </button>
  );
}
